import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils';
import {Menu, X, Facebook, Instagram, MapPin, Phone, Mail} from 'lucide-react';
import { Chatbot } from './Chatbot';

const Navbar = () => {
  const [isOpen, setIsOpen] = React.useState(false);

  const navLinks = [
    { name: 'Accueil', path: '/' },
    { name: 'Le Club', path: '/club' },
    { name: 'Notre App', path: '/app' },
    { name: 'Boutique', path: '/boutique' },
    { name: 'Planning', path: '/planning' },
    { name: 'Tarifs', path: '/tarifs' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <nav className="fixed top-0 w-full z-50 bg-black/90 backdrop-blur-md border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div className="flex-shrink-0">
            <Link to="/" className="flex flex-col items-start">
              <span className="text-3xl font-black italic tracking-tighter text-white leading-none">
                NO <span className="text-nolimit-red">LIMIT</span>
              </span>
              <span className="text-xs text-gray-400 tracking-widest uppercase ml-0.5">Fitness</span>
            </Link>
          </div>
          
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className="text-gray-300 hover:text-nolimit-red px-3 py-2 rounded-md text-sm font-medium transition-colors uppercase tracking-wide"
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/contact"
                className="bg-nolimit-red text-white px-5 py-2 rounded-full text-sm font-bold hover:bg-red-600 transition-all transform hover:scale-105"
              >
                Essai Gratuit
              </Link>
            </div>
          </div>

          <div className="-mr-2 flex md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-800 focus:outline-none"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden bg-black border-b border-white/10">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className="text-gray-300 hover:text-nolimit-red block px-3 py-2 rounded-md text-base font-medium"
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/contact"
              onClick={() => setIsOpen(false)}
              className="block w-full text-center bg-nolimit-red text-white px-5 py-3 rounded-md text-base font-bold mt-4"
            >
              RÉSERVER MA SÉANCE
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

const Footer = () => {
  return (
    <footer className="bg-black border-t border-white/10 pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="space-y-4">
            <Link to="/">
              <h3 className="text-2xl font-black italic text-white leading-none">
                NO <span className="text-nolimit-red">LIMIT</span>
              </h3>
              <span className="text-xs text-gray-400 tracking-widest uppercase block mt-1">Fitness</span>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed">
              Votre salle de sport de référence près de Toul. Musculation, cardio, cours collectifs et coaching personnalisé dans une ambiance conviviale.
            </p>
            <div className="flex space-x-4">
              <a href="https://www.facebook.com/p/NO-LIMIT-fitness-100040814987721/?locale=fr_FR" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-nolimit-red transition-colors">
                <Facebook className="h-6 w-6" />
              </a>
              <a href="https://www.instagram.com/nolimitfitness_54/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-nolimit-red transition-colors">
                <Instagram className="h-6 w-6" />
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-white font-bold uppercase tracking-wider mb-6">Navigation</h4>
            <ul className="space-y-3 text-gray-400 text-sm">
              <li><Link to="/club" className="hover:text-nolimit-red transition-colors">Le Club</Link></li>
              <li><Link to="/boutique" className="hover:text-nolimit-red transition-colors">Boutique</Link></li>
              <li><Link to="/planning" className="hover:text-nolimit-red transition-colors">Planning</Link></li>
              <li><Link to="/tarifs" className="hover:text-nolimit-red transition-colors">Tarifs</Link></li>
              <li><Link to="/faq" className="hover:text-nolimit-red transition-colors">FAQ</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold uppercase tracking-wider mb-6">Horaires</h4>
            <ul className="space-y-3 text-gray-400 text-sm">
              <li className="flex justify-between">
                <span>Lundi - Dimanche</span>
                <span className="text-white">06:00 - 23:00</span>
              </li>
              <li className="text-xs text-gray-500 mt-2">Ouvert 7j/7 toute l'année</li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold uppercase tracking-wider mb-6">Contact</h4>
            <ul className="space-y-4 text-gray-400 text-sm">
              <li className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 text-nolimit-red shrink-0" />
                <span>155 Rue Robert Schumann<br />54200 Écrouves</span>
              </li>
              <li className="flex items-center space-x-3">
                <Phone className="h-5 w-5 text-nolimit-red shrink-0" />
                <span>03 83 64 00 00</span>
              </li>
              <li className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-nolimit-red shrink-0" />
                <span>contact@nolimit-fitness.fr</span>
              </li>
            </ul>
          </div>
        </div>
        <div className="mt-16 pt-8 border-t border-white/10 text-center text-gray-600 text-sm">
          <p>&copy; {new Date().getFullYear()} No Limit Fitness. Tous droits réservés.</p>
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="min-h-screen bg-black text-gray-100 font-sans selection:bg-nolimit-red selection:text-white">
      <Navbar />
      <main className="pt-20">{children}</main>
      <Footer />
      <Chatbot />
    </div>
  );
};
